import { useState, useEffect, useCallback } from "react";
import { useSocket } from "./useSocket.js";

/**
 * useStorePresence — shared "is a human from the store online?" signal.
 *
 * Powers the bubble's online dot and gates the call button.
 *
 * ── The bug this fixes ──────────────────────────────────────────────────────
 *
 * This used to be event-only: it started at `false` and flipped to `true` only if
 * it RECEIVED a `user:online` event. But the server emits `user:online` exactly
 * once — the moment a merchant's socket connects — to the conversation rooms they
 * happened to be in at that instant.
 *
 * So a merchant who signs in at 09:00 and a visitor who arrives at 14:00 never
 * meet: the event fired five hours before the visitor was listening. The store
 * showed OFFLINE permanently, with staff sitting right there in the dashboard.
 *
 * And for a brand-new conversation it could never work at all — the merchant was
 * not in that room when they connected, so the event had nowhere to land.
 *
 * The fix is to ASK on connect (`store:presence:get`) rather than only waiting to
 * be told, and to keep listening for `store:presence` so the dot updates live as
 * staff come and go.
 */
export function useStorePresence() {
    const {
        emit,
        on,
        off,
        onUserOnline,
        onUserOffline,
        onMessageNew,
        onTypingStart,
    } = useSocket();

    const [isStoreOnline, setIsStoreOnline] = useState(false);

    const getStoreId = useCallback(() => {
        try {
            return (
                import.meta.env.VITE_SALESIVE_SHOP_ID ||
                localStorage.getItem("SALESIVE_SHOP_ID") ||
                null
            );
        } catch {
            return null;
        }
    }, []);

    // Ask, don't wait. Answers the question the events cannot: "is anyone home
    // RIGHT NOW", regardless of when they arrived.
    const refresh = useCallback(() => {
        const storeId = getStoreId();
        if (!storeId) return;

        try {
            emit("store:presence:get", { storeId }, (result) => {
                if (result && typeof result.online === "boolean") {
                    setIsStoreOnline(result.online);
                }
            });
        } catch {
            // socket not ready yet — the connect handler below will retry
        }
    }, [emit, getStoreId]);

    useEffect(() => {
        // NOTE: useSocket's `on` returns undefined — unlike onUserOnline() and
        // friends, it is NOT a subscribe-and-return-cleanup helper. These two have
        // to be torn down with off(), or every remount stacks another listener.
        const handlePresence = (payload) => {
            if (payload && typeof payload.online === "boolean") {
                setIsStoreOnline(payload.online);
            }
        };

        // Seed the initial state, and re-seed on every reconnect (a dropped socket
        // must not leave the dot stuck on a stale value).
        refresh();
        on("connect", refresh);

        // Live updates as staff arrive and leave.
        on("store:presence", handlePresence);

        // The original signals still count: a message or a keystroke from the store
        // is proof a human is there, whatever presence says.
        const handleActivity = () => setIsStoreOnline(true);

        const cleanupOnline = onUserOnline(() => setIsStoreOnline(true));
        // Someone leaving does NOT mean the store is empty — a colleague may still
        // be on. Re-ask instead of assuming.
        const cleanupOffline = onUserOffline(refresh);
        const cleanupMessage = onMessageNew(handleActivity);
        const cleanupTyping = onTypingStart(handleActivity);

        return () => {
            off("connect", refresh);
            off("store:presence", handlePresence);
            cleanupOnline();
            cleanupOffline();
            cleanupMessage();
            cleanupTyping();
        };
    }, [
        on,
        off,
        refresh,
        onUserOnline,
        onUserOffline,
        onMessageNew,
        onTypingStart,
    ]);

    return isStoreOnline;
}

export default useStorePresence;
